import Button, { TYPES } from 'components/Button'
import IconV2, { ICON_TYPES } from 'components/Icon.v2'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Map, List } from 'immutable'
import _startCase from 'lodash/startCase'
import { FILTER_APPLIED_GA4_EVENT } from 'services/event-tracking'
import { filterOptionsCompare } from 'services/filter-set'
import { getPathLocationName } from 'services/url'
import SeoNavigationLinks from 'components/SeoNavigationLinks'
import FiltersV2Option from './Filters.v2Option'
import {
  getFilterSelectedOption,
  getSeoLinks,
  getUrlChangeType,
} from './utils'

/**
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text data
 * @param {Map} props.filterData - The filter data
 * @param {String} props.language - The user's language
 * @param {Object} props.location - The location object
 * @param {Function} props.onOptionSelected - The selected option handler
 * @param {Function} props.setDefaultGa4Event - The GA4 event action
 * @param {Function} props.isOpenedByDefault - The value indicating if the filter
 * @param {String} props.defaultValue - The defauult filter value
 * should be displayed as default expanded for mobile
 * @returns {JSX.Element} The Filter Dropdown component
 */

function FiltersV2Dropdown ({
  staticText,
  filterData = Map(),
  language,
  location,
  onOptionSelected,
  setDefaultGa4Event,
  isOpenedByDefault,
  defaultValue,
}) {
  const dropdownRef = useRef(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const options = filterData.getIn(['filter', 'options'], List())
  const param = filterData.get('param')
  const label = staticText.getIn(['filterSetLabels', filterData.get('type'), 'label'])
  const isColumn = options.size < 10
  const selectedOption = useMemo(
    () => getFilterSelectedOption({
      options,
      location,
      param,
      defaultValue,
    }) || Map(),
    [options, location, param],
  )
  const selectedOptionValue = selectedOption.get('value')
  const selectedOptionName = selectedOption.get('name')
  const seoLinks = useMemo(
    () => getSeoLinks(options, location),
    [options, location],
  )
  const sortedOptions = useMemo(() => {
    /* Duration options are sorted as strings in data so
      5 minutes value is between 45 and 60 minutes
    */
    if (param === 'duration') {
      return options.sortBy((item) => item, (a, b) => filterOptionsCompare(a, b, language))
    }
    return options
  }, [options, param, language])

  const dropdownClassName = [
    'filters-v2__dropdown',
    isColumn ? 'filters-v2__dropdown--column' : null,
  ].filter((cls) => !!cls).join(' ')

  const containerClassName = [
    'filters-v2__dropdown-container',
    isExpanded ? 'filters-v2__dropdown-container--expanded' : null,
    isOpenedByDefault ? 'filters-v2__dropdown-container--opened' : null,
  ].filter((cls) => !!cls).join(' ')

  const optionsClassName = [
    'filters-v2__dropdown-options',
    isColumn ? 'filters-v2__dropdown-options--column' : null,
  ].filter((cls) => !!cls).join(' ')

  const buttonClassName = [
    'filters-v2__dropdown-button',
    isOpenedByDefault ? 'filters-v2__dropdown-button--mobile-hidden' : null,
    `test-filter-dropdown-${label}`,
  ].filter((cls) => !!cls).join(' ')

  const handleOptionClick = useCallback((option) => {
    const changeType = getUrlChangeType(location, options, option)
    const uiLocation = getPathLocationName(location.pathname)

    // GA4 event
    setDefaultGa4Event(FILTER_APPLIED_GA4_EVENT.mergeDeep({
      ui_element: _startCase(param.replace('_', ' ')),
      ui_location: uiLocation,
      filter_setting: option.get('name'),
    }))

    setIsExpanded(false)
    onOptionSelected(option, param, changeType)
  }, [location, options, param])

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsExpanded(false)
    }
  }, [dropdownRef])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  return (
    <div className={dropdownClassName} ref={dropdownRef}>
      <Button
        type={TYPES.DEFAULT}
        onClick={() => setIsExpanded(!isExpanded)}
        className={buttonClassName}
      >
        <span className="filters-v2__dropdown-label filters-v2__dropdown-label--small">
          {label}
        </span>
        <span className="filters-v2__dropdown-label filters-v2__dropdown-label--large">
          {selectedOptionValue
            ? <>
              <span>{label}</span>
              <span>{selectedOptionName}</span>
            </>
            : label}
        </span>
        <IconV2
          className="filters-v2__dropdown-icon"
          type={isExpanded ? ICON_TYPES.CHEVRON_UP : ICON_TYPES.CHEVRON_DOWN}
        />
      </Button>
      <div className={containerClassName}>
        <div className="filters-v2__dropdown-options-wrapper">
          <ul className={optionsClassName}>
            {sortedOptions.map((option) => (
              <FiltersV2Option
                key={option.get('value')}
                name={option.get('name')}
                onClick={() => handleOptionClick(option)}
                isActive={selectedOptionValue === option.get('value')}
              />
            ))}
          </ul>
        </div>
      </div>
      {seoLinks.size > 0 && <SeoNavigationLinks links={seoLinks} />}
    </div>
  )
}

export default FiltersV2Dropdown
